/**
 * Trash.
 *
 * Not in the prototype — the sidebar has a row and no page behind it. Built
 * because encrypted data is unrecoverable once purged: without a trash, a
 * mis-click is permanent and no support request can undo it.
 *
 * Trashed items still count against quota, which matches the design's storage
 * breakdown listing "Trash" as its own line. The page says so, because
 * otherwise "delete a file, storage unchanged" reads as a bug.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, errorText, type NodeWire } from '../api';
import { ds } from '../ds';
import { useApp } from '../state';
import {
  DataRow,
  DataTable,
  Footnote,
  PageTitle,
  Panel,
  type Column
} from '../components/Shell';
import { TypeTile } from './Files';
import { decryptNode, getCachedKey, type DecryptedNode } from '../nodes';
import { formatBytes, formatWhen, monoPlain } from '../theme';
import { SkeletonRows } from '../components/Skeleton';
import { useT } from '../i18n';

const TRASH_COLUMNS: Column[] = [
  { width: 38 },
  { label: 'Name', width: 'flex' },
  // The same widths as Files, so the same thresholds.
  { label: 'Size', width: 90, align: 'right', hideBelow: 720 },
  { label: 'Deleted', width: 116, align: 'right', hideBelow: 600 },
  // A "Restore" button plus the purge icon.
  { width: 140 }
];

export function Trash() {
  const t = useT();
  const { Button, Icon } = ds();
  const { refreshStorage } = useApp();

  const [nodes, setNodes] = useState<DecryptedNode[]>([]);
  const [retention, setRetention] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { nodes: wire, retentionDays } = await api.trash.list();
      setRetention(retentionDays);
      setNodes(await Promise.all(wire.map(decryptTrashed)));
    } catch (err) {
      setError(errorText(err, 'Could not load the trash.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const totalBytes = nodes.reduce((n, node) => n + node.plainSizeBytes, 0);

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16 }}>
        <PageTitle
          title={t('trash.trash')}
          meta={`${nodes.length} item${nodes.length === 1 ? '' : 's'} · ${formatBytes(totalBytes)} · deleted for good after ${retention} days`}
        />
        {nodes.length > 0 && (
          <Button
            variant="danger"
            size="md"
            onClick={async () => {
              if (
                !window.confirm(
                  'Empty the trash? This cannot be undone.'
                )
              )
                return;
              await api.trash.empty();
              await load();
              await refreshStorage();
            }}
          >
            {t('trash.empty-trash')}
          </Button>
        )}
      </div>

      {error && (
        <Panel style={{ padding: '14px 20px', background: 'var(--danger-100)' }}>
          <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error}</span>
        </Panel>
      )}

      {loading ? (
        <Panel style={{ padding: 40 }}>
          <SkeletonRows rows={4} label={t('trash.loading-the-trash')} />
        </Panel>
      ) : (
        <DataTable
          columns={TRASH_COLUMNS}
          empty={nodes.length === 0 ? <span style={monoPlain}>{t('trash.nothing-in-the-trash')}</span> : undefined}
        >
          {nodes.map((node, i) => (
            <DataRow
              key={node.id}
              columns={TRASH_COLUMNS}
              first={i === 0}
              tile={
                // Dimmed rather than differently coloured: still recognisably
                // the same file, visibly not live.
                <span style={{ opacity: 0.6, display: 'flex' }}>
                  <TypeTile node={node} />
                </span>
              }
              cells={[
                <span
                  style={{
                    display: 'block',
                    fontSize: 14.5,
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {node.name}
                </span>,
                node.kind === 'folder' ? '—' : formatBytes(node.plainSizeBytes),
                node.trashedAt ? formatWhen(node.trashedAt) : '—'
              ]}
              actions={
                <>
                  <Button
                    variant="soft"
                    size="sm"
                    onClick={async () => {
                      try {
                        await api.nodes.restore(node.id);
                        await load();
                      } catch (err) {
                        setError(errorText(err, 'Could not restore that.'));
                      }
                    }}
                  >
                    Restore
                  </Button>
                  <button
                    aria-label={t('trash.delete-for-good')}
                    title={t('trash.delete-for-good')}
                    onClick={async () => {
                      if (!window.confirm(`Delete “${node.name}” for good? This cannot be undone.`))
                        return;
                      await api.nodes.purge(node.id);
                      await load();
                      await refreshStorage();
                    }}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 10,
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--danger-600)',
                      cursor: 'pointer'
                    }}
                  >
                    <Icon name="trash-2" size={15} />
                  </button>
                </>
              }
            />
          ))}
        </DataTable>
      )}

      <Footnote>
        Items in the trash count against your storage. They are purged after {retention} days.
      </Footnote>
    </div>
  );
}

/**
 * Decrypt a trashed node, best-effort.
 *
 * The trash spans folders, so the keys for every item are not necessarily
 * loaded — a name that cannot be decrypted here is shown by id rather than
 * hidden, because the user still has to be able to restore or purge it.
 */
async function decryptTrashed(node: NodeWire): Promise<DecryptedNode> {
  const key = getCachedKey(node.id);
  if (key) return decryptNode(node, key);

  return {
    id: node.id,
    parentId: node.parentId,
    kind: node.kind,
    name: `${node.kind === 'folder' ? 'Folder' : 'File'} · ${node.id.slice(-8)}`,
    mime: null,
    sizeBytes: node.sizeBytes,
    plainSizeBytes: node.plainSizeBytes,
    hasThumbnail: node.hasThumbnail,
    hasContent: !!node.currentRevisionId,
    trashedAt: node.trashedAt,
    createdAt: node.createdAt,
    updatedAt: node.updatedAt,
    role: node.role
  };
}
